import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, filter, map, Observable, take } from 'rxjs';

import { EnvironmentService, UaService } from '@ecm/consumer';
import { EcmDispatcherModule, PageInformation } from '@ecm/dispatcher';

interface PageInformationWithTime extends PageInformation {
    time: number;
}

@Injectable({
    providedIn: 'root',
})
export class PerformanceService {
    public constructor(
        private router: Router,
        private http: HttpClient,
        private ngZone: NgZone,
        private environmentService: EnvironmentService,
        private uaService: UaService,
    ) {}

    public init() {
        combineLatest([this.getFirstPageLoadedInformationWithTime(), this.getOnlyFirstNavigationEnd()]).subscribe({
            next: ([pageInfo]) => this.sendLog(pageInfo),
            error: () => null,
        });
    }

    private getFirstPageLoadedInformationWithTime(): Observable<PageInformationWithTime> {
        return EcmDispatcherModule.pageLoadedEvents.pipe(
            map((pageInfo: PageInformation): PageInformationWithTime => {
                return { ...pageInfo, time: globalThis.performance.now() };
            }),
            take(1),
        );
    }

    private getOnlyFirstNavigationEnd(): Observable<NavigationEnd> {
        return this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event, index) => {
                if (index) {
                    throw new Error('Not first NavEnd');
                } else {
                    return event;
                }
            }),
        );
    }

    private sendLog(pageInfo: PageInformationWithTime) {
        const { ENDPOINT, LOAD } = this.environmentService.instant('API.PERF_TRACKER') || {};
        if (ENDPOINT === false || isNaN(Number(LOAD)) || Math.floor(Math.random() * 100) >= Number(LOAD)) {
            return;
        }
        const headers = new HttpHeaders()
            .set('X-Perf-Tracking-Kenobi', pageInfo.kenobiCode)
            .set('X-Perf-Tracking-Time', pageInfo.time.toString())
            .set('X-Perf-Tracking-Comment', pageInfo.comment || '')
            .set('X-Perf-Tracking-Browser', this.uaService.getBrowserName() + ' ' + this.uaService.getBrowserVersion())
            .set('X-Perf-Tracking-Device', this.uaService.getDeviceType())
            .set('X-Perf-Tracking-Os', this.uaService.getOSName() + ' ' + this.uaService.getOSVersion());
        this.ngZone.runOutsideAngular(() => {
            this.http.get(ENDPOINT, { headers }).subscribe({ error: () => null });
        });
    }
}
